import { useTranslation } from 'react-i18next'

export const SUPPORTED_LANGUAGES = ['sk', 'en', 'ua'] as const

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

export const DEFAULT_LANGUAGE: SupportedLanguage = 'sk'

/** Short labels for the compact switcher pill. */
export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  sk: 'SK',
  en: 'EN',
  ua: 'UA'
}

/**
 * BCP-47 tags for Intl / toLocaleDateString. Note that Ukrainian is `uk` in
 * BCP-47 — `ua` is a country code and would silently fall back to the default
 * locale, formatting dates and numbers in the wrong language.
 */
const INTL_LOCALES: Record<SupportedLanguage, string> = {
  sk: 'sk-SK',
  en: 'en-GB',
  ua: 'uk-UA'
}

/**
 * The code i18next itself runs on, which is not always the code this app uses.
 *
 * i18next picks plural forms via Intl.PluralRules, and Intl.PluralRules('ua')
 * resolves to en-US rather than throwing — Ukrainian would silently get English
 * pluralisation, so `2 повідомлення` would take the _other form instead of _few.
 * Running i18next on 'uk' fixes that. Everything the user or the API sees stays
 * 'ua': the URL prefix, preferred_language, localStorage, the locale directory.
 */
const I18NEXT_CODES: Record<SupportedLanguage, string> = {
  sk: 'sk',
  en: 'en',
  ua: 'uk'
}

export const toI18nextCode = (language: SupportedLanguage): string =>
  I18NEXT_CODES[language]

const FROM_I18NEXT_CODES: Record<string, SupportedLanguage> =
  Object.fromEntries(
    SUPPORTED_LANGUAGES.map((lang) => [I18NEXT_CODES[lang], lang])
  )

/** Path to the terms page on the public website, per language. */
const TERMS_PATHS: Record<SupportedLanguage, string> = {
  sk: '/obchodne-podmienky',
  en: '/en/terms',
  ua: '/ua/umovy'
}

export const isSupportedLanguage = (
  value: string | null | undefined
): value is SupportedLanguage =>
  !!value && (SUPPORTED_LANGUAGES as readonly string[]).includes(value)

/**
 * Narrow an arbitrary language string to one this app supports. Accepts both
 * app codes ('ua') and what i18next reports back ('uk', or a regional variant
 * like 'en-GB'), so callers never have to know which side they are holding.
 */
export const resolveLanguage = (
  value: string | null | undefined
): SupportedLanguage => {
  if (isSupportedLanguage(value)) return value
  if (value && FROM_I18NEXT_CODES[value]) return FROM_I18NEXT_CODES[value]
  const base = value?.split('-')[0]
  if (isSupportedLanguage(base)) return base
  if (base && FROM_I18NEXT_CODES[base]) return FROM_I18NEXT_CODES[base]
  return DEFAULT_LANGUAGE
}

export const getIntlLocale = (value: string | null | undefined): string =>
  INTL_LOCALES[resolveLanguage(value)]

export const getTermsUrl = (
  websiteUrl: string,
  value: string | null | undefined
): string => `${websiteUrl}${TERMS_PATHS[resolveLanguage(value)]}`

/** The locale to hand to Intl / toLocaleDateString for the current language. */
export const useIntlLocale = (): string => {
  const { i18n } = useTranslation()
  return getIntlLocale(i18n.language)
}

/** The current language, narrowed to one we actually support. */
export const useCurrentLanguage = (): SupportedLanguage => {
  const { i18n } = useTranslation()
  return resolveLanguage(i18n.language)
}
